import type { QueryClient } from '@tanstack/react-query'
import { ApiError } from '@/api/client'
import { getProgress, listEpisodes, saveProgress } from '@/api/episodes'
import type { EpisodeResponse, PlaybackEvent, PlaybackProgressResponse } from '@/api/types'
import type { NotifySeverity } from '@/components/Notify'
// Вне React: i18n.t в момент показа — текст на текущем языке.
import { i18n } from '@/i18n'
import { setUpdateBlocker } from '@/lib/pwa'
import {
  loadAutoplayNext,
  loadPlaybackRate,
  saveAutoplayNext,
  savePlaybackRate,
} from './preferences'
import {
  CLIENT_ID,
  findNextEpisode,
  mediaKind,
  patchEpisodeProgress,
  presignedExpiresAt,
  resolveStartPosition,
  updatedAtMs,
} from './progress'
import { subscribePlaybackSignals, type PlaybackSignal } from './sync'
import {
  setMediaSessionPlaybackState,
  syncPositionState,
  type MediaSessionHandlers,
} from './useMediaSession'

// Редко меняющееся состояние плеера — то, что видят React-компоненты. Частое (время,
// буфер) сюда не попадает: его читают контролы дока через usePlayer(selector) Video.js.
export interface PlayerUiState {
  // Снапшот эпизода на момент play(): enclosureUrl отсюда, а не из кэша ['episodes'] —
  // каждый рефетч списка выдаёт новые presigned-ссылки, src перепривязывать нельзя.
  episode: EpisodeResponse | null
  expanded: boolean
  rate: number
  autoplayNext: boolean
}

export interface PlayerActions {
  play: (episode: EpisodeResponse) => void
  toggle: () => void
  seekBy: (deltaSec: number) => void
  stop: () => void
  setExpanded: (expanded: boolean) => void
  setRate: (rate: number) => void
  setAutoplayNext: (enabled: boolean) => void
  // Только для PlayerDock: ref медиа-элемента (<Audio>/<Video> Video.js).
  attachMedia: (media: HTMLMediaElement | null) => void | (() => void)
}

type Notify = (message: string, severity?: NotifySeverity) => void

// Штатная частота сохранения во время игры — сервер копит её в памяти (write-behind).
const SAVE_INTERVAL_MS = 10_000
const SEEK_SAVE_DEBOUNCE_MS = 1_500
const POSITION_STATE_INTERVAL_MS = 5_000
// Сразу после старта своего воспроизведения чужие события нас не паузят. Так «побеждает»
// устройство, запустившее воспроизведение последним, даже если периодический PUT другого
// устройства разминулся в сети с нашим стартовым.
const START_GRACE_MS = 3_000
// Повтор после ошибки медиа — один на инцидент: новая ошибка вскоре после восстановления
// (кодек не поддерживается, свежая ссылка тоже битая) = сдаёмся, а не крутим цикл.
const RECOVERY_WINDOW_MS = 30_000

/**
 * Императивное ядро плеера: управляет HTMLMediaElement напрямую (Video.js-стор синхронизируется
 * с ним сам по media-событиям), сохраняет и синхронизирует позицию прослушивания.
 *
 * Живёт в PlayerProvider (основной чанк), а медиа-элемент — в ленивом PlayerDock: связь через
 * attachMedia. React видит только PlayerUiState через subscribe/getUi (useSyncExternalStore).
 */
export class PlaybackController {
  readonly actions: PlayerActions
  readonly mediaSessionHandlers: MediaSessionHandlers

  private readonly queryClient: QueryClient
  private readonly notify: Notify
  private ui: PlayerUiState
  private readonly listeners = new Set<() => void>()

  private media: HTMLMediaElement | null = null
  private mediaListeners: AbortController | null = null
  private episode: EpisodeResponse | null = null
  // Какой эпизод в какой элемент загружен. Расходится с episode/media, пока новый эпизод
  // ждёт элемента другого типа (audio↔video) или элемент пересоздан: события такого
  // элемента не наши — иначе пустой/чужой currentTime (0) ушёл бы на сервер.
  private source: { media: HTMLMediaElement; episodeId: string } | null = null
  // Загрузка, ждущая подходящего медиа-элемента.
  private pendingLoad: { autoplay: boolean; resumeAt: number | null } | null = null
  // Прогресс, с которого стартуем: сперва из кэша списка, уточняется GET …/progress.
  private startProgress: PlaybackProgressResponse | null = null
  // Явная позиция старта после восстановления источника (важнее startProgress).
  private resumeAt: number | null = null
  // Стартовая позиция применена (loadedmetadata) — до этого не сохраняем, иначе затрём 0.
  private startApplied = false
  // Позиция менялась воспроизведением/перемоткой пользователя с последнего сохранения.
  private dirty = false
  private lastSaveAt = 0
  private seekSaveTimer: number | undefined
  private lastPositionStateAt = 0
  private lastPosition = 0
  // Цель нашего собственного seek — его seeked не сохраняем (иначе эхо на другие устройства).
  private programmaticSeek: number | null = null
  // Следующий pause — «тихий» (синхронизация с другим устройством): не сохраняем, иначе пинг-понг.
  private silentPause = false
  private wantPlaying = false
  private playingSince = Number.NEGATIVE_INFINITY
  private recoveredAt = Number.NEGATIVE_INFINITY
  // Самый свежий известный updatedAt (мс) по эпизодам — из списка, GET, ответов PUT и WS.
  private readonly knownUpdatedAt = new Map<string, number>()

  constructor(queryClient: QueryClient, notify: Notify) {
    this.queryClient = queryClient
    this.notify = notify
    this.ui = {
      episode: null,
      expanded: false,
      rate: loadPlaybackRate(),
      autoplayNext: loadAutoplayNext(),
    }
    this.actions = {
      play: (episode) => this.play(episode),
      toggle: () => this.toggle(),
      seekBy: (delta) => this.seekBy(delta),
      stop: () => this.stop(true),
      setExpanded: (expanded) => this.update({ expanded }),
      setRate: (rate) => this.setRate(rate),
      setAutoplayNext: (enabled) => this.setAutoplayNext(enabled),
      attachMedia: (media) => this.attachMedia(media),
    }
    this.mediaSessionHandlers = {
      play: () => this.resume(),
      pause: () => this.media?.pause(),
      seekBy: (delta) => this.seekBy(delta),
      seekTo: (time) => this.seekTo(time),
      next: () => this.playNext(),
    }
  }

  // === useSyncExternalStore ===
  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  readonly getUi = (): PlayerUiState => this.ui

  private update(patch: Partial<PlayerUiState>): void {
    this.ui = { ...this.ui, ...patch }
    for (const listener of this.listeners) listener()
  }

  // Подписки уровня документа. Cleanup = размонтирование AppLayout (logout): стоп и
  // очистка src, без сохранения — токенов уже нет.
  mount(): () => void {
    const unsubscribe = subscribePlaybackSignals((signal) => this.onSignal(signal))
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') this.save({ keepalive: true })
      else void this.resync()
    }
    const onPageHide = () => this.save({ keepalive: true })
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pagehide', onPageHide)
    setUpdateBlocker(() => this.isPlaying())
    return () => {
      unsubscribe()
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('pagehide', onPageHide)
      setUpdateBlocker(null)
      this.stop(false)
    }
  }

  // === Медиа-элемент ===

  private attachMedia(media: HTMLMediaElement | null): void | (() => void) {
    if (!media) {
      if (this.media) this.detachMedia(this.media)
      return
    }
    if (this.media !== media) {
      if (this.media) this.detachMedia(this.media)
      this.media = media
      this.listenMedia(media)
      media.defaultPlaybackRate = this.ui.rate
      media.playbackRate = this.ui.rate
      // Элемент пересоздан при уже загруженном эпизоде (не смена audio↔video — та идёт через
      // pendingLoad): продолжаем с той же позиции, как было. Тот же элемент, вернувшийся
      // после пересборки ref (StrictMode), источник сохранил — его не трогаем.
      const source = this.source
      if (
        !this.pendingLoad &&
        this.episode &&
        source?.episodeId === this.episode.id &&
        source.media !== media
      ) {
        this.pendingLoad = {
          autoplay: this.wantPlaying,
          resumeAt: this.startApplied ? this.lastPosition : null,
        }
      }
      this.loadPending()
    }
    return () => this.detachMedia(media)
  }

  private detachMedia(media: HTMLMediaElement): void {
    if (this.media !== media) return
    this.mediaListeners?.abort()
    this.mediaListeners = null
    this.media = null
    // Элемент уходит из DOM (смена audio↔video, стоп, logout) — гасим и отпускаем источник.
    // В микрозадаче: если тот же элемент тут же вернулся (пересборка ref), его не трогаем.
    queueMicrotask(() => {
      if (this.media === media) return
      media.pause()
      media.removeAttribute('src')
      media.load()
    })
  }

  private listenMedia(media: HTMLMediaElement): void {
    const controller = new AbortController()
    this.mediaListeners = controller
    const on = (type: keyof HTMLMediaElementEventMap, handler: () => void) =>
      media.addEventListener(type, handler, { signal: controller.signal })
    on('loadedmetadata', () => this.onLoadedMetadata(media))
    on('play', () => this.onPlay())
    on('playing', () => this.onPlaying(media))
    on('pause', () => this.onPause(media))
    on('timeupdate', () => this.onTimeUpdate(media))
    on('seeked', () => this.onSeeked(media))
    on('ratechange', () => syncPositionState(media))
    on('ended', () => this.onEnded(media))
    on('error', () => void this.recover())
    on('waiting', () => this.onStall())
    on('stalled', () => this.onStall())
  }

  // Загрузить отложенный источник, если медиа-элемент подходящего типа на месте.
  private loadPending(): void {
    const media = this.media
    const episode = this.episode
    const pending = this.pendingLoad
    if (!media || !episode || !pending) return
    const kind = media instanceof HTMLVideoElement ? 'video' : 'audio'
    if (kind !== mediaKind(episode)) return
    this.pendingLoad = null
    this.source = { media, episodeId: episode.id }
    this.resumeAt = pending.resumeAt
    this.startApplied = false
    this.programmaticSeek = null
    // load-алгоритм сбрасывает playbackRate в defaultPlaybackRate — выставляем оба.
    media.defaultPlaybackRate = this.ui.rate
    media.src = episode.enclosureUrl
    media.playbackRate = this.ui.rate
    if (pending.autoplay) this.resume()
  }

  // События медиа относятся к текущему эпизоду, только если загружен именно его источник.
  private owns(): boolean {
    const source = this.source
    return (
      source !== null &&
      this.episode !== null &&
      source.media === this.media &&
      source.episodeId === this.episode.id
    )
  }

  private onLoadedMetadata(media: HTMLMediaElement): void {
    if (!this.owns()) return
    syncPositionState(media)
    if (this.startApplied) return
    this.startApplied = true
    const target = this.resumeAt ?? resolveStartPosition(this.startProgress, media.duration)
    this.resumeAt = null
    this.lastPosition = target
    // Отсчёт 10-секундного интервала — от старта: первое сохранение сделает onPlaying,
    // timeupdate до него не должен слать дубль.
    this.lastSaveAt = performance.now()
    if (target > 0) this.seekSilently(target)
  }

  // Намерение играть — от любого источника (контролы и хоткеи Video.js, Media Session,
  // наш resume): по нему решают восстановление и «продолжить, если играло».
  private onPlay(): void {
    setMediaSessionPlaybackState('playing')
    if (this.owns()) this.wantPlaying = true
  }

  private onPlaying(media: HTMLMediaElement): void {
    if (!this.owns() || !this.startApplied) return
    this.wantPlaying = true
    this.playingSince = performance.now()
    syncPositionState(media)
    // Сразу заявляем «играю здесь»: другие устройства на этом эпизоде встанут на паузу.
    this.save({ force: true })
  }

  private onPause(media: HTMLMediaElement): void {
    setMediaSessionPlaybackState('paused')
    if (!this.owns()) return
    syncPositionState(media)
    if (this.silentPause) {
      this.silentPause = false
      this.wantPlaying = false
      return
    }
    // pause приходит и при достижении конца — перед ended; сохранит onEnded (completed).
    if (media.ended) return
    this.wantPlaying = false
    this.save()
  }

  private onTimeUpdate(media: HTMLMediaElement): void {
    if (!this.owns() || !this.startApplied) return
    this.lastPosition = media.currentTime
    if (media.paused || media.seeking) return
    this.dirty = true
    const now = performance.now()
    if (now - this.lastSaveAt >= SAVE_INTERVAL_MS) this.save()
    if (now - this.lastPositionStateAt >= POSITION_STATE_INTERVAL_MS) {
      this.lastPositionStateAt = now
      syncPositionState(media)
    }
  }

  private onSeeked(media: HTMLMediaElement): void {
    if (!this.owns()) return
    syncPositionState(media)
    this.lastPosition = media.currentTime
    const own = this.programmaticSeek
    this.programmaticSeek = null
    if (own !== null && Math.abs(media.currentTime - own) < 1) return
    if (!this.startApplied) return
    this.dirty = true
    window.clearTimeout(this.seekSaveTimer)
    this.seekSaveTimer = window.setTimeout(() => this.save(), SEEK_SAVE_DEBOUNCE_MS)
  }

  private onEnded(media: HTMLMediaElement): void {
    const episode = this.episode
    if (!episode || !this.owns()) return
    this.wantPlaying = false
    syncPositionState(media)
    this.save({ completed: true })
    if (!this.ui.autoplayNext) return
    const next = findNextEpisode(
      this.queryClient.getQueryData<EpisodeResponse[]>(['episodes']),
      episode.id,
    )
    if (next) this.play(next)
  }

  // Застряли на подгрузке с уже истёкшей presigned-ссылкой. Chromium блокирует XML-ответ 403
  // от S3 через ORB и бесконечно ретраит range-запросы — error так и не приходит, плеер
  // просто висит. Срок ссылки виден в ней самой — восстанавливаемся, не дожидаясь error.
  private onStall(): void {
    const episode = this.episode
    if (!episode || !this.owns() || !this.wantPlaying) return
    const expiresAt = presignedExpiresAt(episode.enclosureUrl)
    if (expiresAt !== null && Date.now() >= expiresAt) void this.recover()
  }

  // Ошибка медиа (или зависание на протухшей ссылке, см. onStall) — чаще всего истёкший
  // presigned-URL. Один повтор: свежий список → новая ссылка → та же позиция → продолжаем,
  // если играло.
  private async recover(): Promise<void> {
    const episode = this.episode
    if (!episode || !this.owns()) return
    const wasPlaying = this.wantPlaying
    const now = performance.now()
    if (now - this.recoveredAt < RECOVERY_WINDOW_MS) {
      this.giveUp(episode)
      return
    }
    this.recoveredAt = now
    // currentTime во время зависшего seek — его цель (seeked так и не придёт), берём её.
    const current = this.media?.currentTime ?? 0
    const resumeAt = this.startApplied ? (current > 0 ? current : this.lastPosition) : null
    let fresh: EpisodeResponse | undefined
    try {
      const list = await this.queryClient.fetchQuery({
        queryKey: ['episodes'],
        queryFn: listEpisodes,
        staleTime: 0,
      })
      fresh = list.find((item) => item.id === episode.id)
    } catch {
      this.giveUp(episode)
      return
    }
    if (this.episode?.id !== episode.id) return
    if (!fresh) {
      this.stop(false)
      this.notify(
        i18n.t(($) => $.player.episodeDeleted),
        'info',
      )
      return
    }
    const refreshed = { ...episode, enclosureUrl: fresh.enclosureUrl }
    this.episode = refreshed
    this.pendingLoad = { autoplay: wasPlaying, resumeAt }
    this.update({ episode: refreshed })
    this.loadPending()
  }

  private giveUp(episode: EpisodeResponse): void {
    this.wantPlaying = false
    this.notify(
      mediaKind(episode) === 'video'
        ? i18n.t(($) => $.player.videoFailed)
        : i18n.t(($) => $.player.episodeFailed),
      'error',
    )
  }

  // === Действия ===

  private play(episode: EpisodeResponse): void {
    if (this.episode?.id === episode.id) {
      this.resume()
      return
    }
    this.save()
    window.clearTimeout(this.seekSaveTimer)
    this.episode = episode
    this.source = null
    this.startProgress = episode.progress ?? null
    this.remember(episode.progress)
    this.dirty = false
    this.lastSaveAt = 0
    this.lastPosition = 0
    this.playingSince = Number.NEGATIVE_INFINITY
    this.recoveredAt = Number.NEGATIVE_INFINITY
    this.pendingLoad = { autoplay: true, resumeAt: null }
    this.update({ episode })
    // Тот же тип медиа — грузим в живой элемент синхронно, ещё внутри клика (iOS требует
    // user gesture для play()). Другой тип — загрузит attachMedia нового элемента.
    this.loadPending()
    void this.fetchStartProgress(episode.id)
  }

  private resume(): void {
    const media = this.media
    if (!media || !this.episode) return
    this.wantPlaying = true
    media.play().catch((err: unknown) => {
      // NotAllowedError — нет user gesture (iOS после смены audio↔video): остаёмся на
      // паузе, пользователь нажмёт play сам. AbortError — источник сменили до старта.
      if (err instanceof DOMException && err.name === 'NotAllowedError') this.wantPlaying = false
    })
  }

  private toggle(): void {
    const media = this.media
    if (!media) return
    if (media.paused) this.resume()
    else media.pause()
  }

  private seekBy(delta: number): void {
    const media = this.media
    if (media) this.seekTo(media.currentTime + delta)
  }

  private seekTo(time: number): void {
    const media = this.media
    if (!media || !this.owns()) return
    const duration = Number.isFinite(media.duration) ? media.duration : Number.POSITIVE_INFINITY
    media.currentTime = Math.min(Math.max(0, time), duration)
  }

  private playNext(): void {
    const episode = this.episode
    if (!episode) return
    const next = findNextEpisode(
      this.queryClient.getQueryData<EpisodeResponse[]>(['episodes']),
      episode.id,
    )
    if (next) this.play(next)
  }

  private stop(save: boolean): void {
    if (save) this.save()
    window.clearTimeout(this.seekSaveTimer)
    const media = this.media
    this.episode = null
    this.source = null
    this.pendingLoad = null
    this.startProgress = null
    this.wantPlaying = false
    if (media) {
      media.pause()
      media.removeAttribute('src')
      media.load()
    }
    if (this.ui.episode || this.ui.expanded) this.update({ episode: null, expanded: false })
  }

  private setRate(rate: number): void {
    savePlaybackRate(rate)
    if (this.media) {
      this.media.defaultPlaybackRate = rate
      this.media.playbackRate = rate
    }
    this.update({ rate })
  }

  private setAutoplayNext(enabled: boolean): void {
    saveAutoplayNext(enabled)
    this.update({ autoplayNext: enabled })
  }

  private isPlaying(): boolean {
    const media = this.media
    return media !== null && !media.paused && !media.ended
  }

  private seekSilently(target: number): void {
    const media = this.media
    if (!media) return
    const duration = Number.isFinite(media.duration) ? media.duration : Number.POSITIVE_INFINITY
    const clamped = Math.min(Math.max(0, target), duration)
    this.lastPosition = clamped
    // Позиция пришла с сервера — сохранять нечего.
    this.dirty = false
    if (Math.abs(media.currentTime - clamped) < 0.5) return
    this.programmaticSeek = clamped
    media.currentTime = clamped
  }

  // === Прогресс ===

  // PUT позиции. Обычные сохранения — только при изменениях (dirty) и с completed: false.
  private save(options: { completed?: boolean; force?: boolean; keepalive?: boolean } = {}): void {
    const { completed = false, force = false, keepalive = false } = options
    const media = this.media
    const episode = this.episode
    if (!media || !episode || !this.owns() || !this.startApplied) return
    if (!completed && !force && !this.dirty) return
    const position =
      completed && Number.isFinite(media.duration) ? media.duration : media.currentTime
    if (!Number.isFinite(position)) return
    this.dirty = false
    this.lastSaveAt = performance.now()
    window.clearTimeout(this.seekSaveTimer)
    const id = episode.id
    saveProgress(
      id,
      { position: Math.max(0, position), completed, clientId: CLIENT_ID },
      { keepalive },
    ).then(
      (progress) => {
        this.accept(progress)
      },
      (err: unknown) => {
        if (this.episode?.id !== id) return
        if (err instanceof ApiError && err.code === 'EPISODE_NOT_FOUND') {
          this.stop(false)
          this.notify(
            i18n.t(($) => $.player.episodeDeleted),
            'info',
          )
          return
        }
        // Сеть/сервер — досохраним следующим тиком.
        this.dirty = true
      },
    )
  }

  private async fetchStartProgress(id: string): Promise<void> {
    let progress: PlaybackProgressResponse
    try {
      progress = await getProgress(id)
    } catch {
      return
    }
    const fresh = this.accept(progress)
    if (!fresh || this.episode?.id !== id) return
    this.startProgress = progress
    const media = this.media
    // Метаданных ещё нет — позиция применится в onLoadedMetadata.
    if (!media || !this.owns() || !this.startApplied || this.dirty) return
    // Метаданные обогнали ответ — стартовали с позиции из кэша списка; поправим, если
    // сервер знает другую, а пользователь ещё ничего не сдвинул.
    const target = resolveStartPosition(progress, media.duration)
    if (Math.abs(media.currentTime - target) > 2) this.seekSilently(target)
  }

  // Принять прогресс с сервера: запомнить updatedAt и пропатчить кэш списка.
  // false — значение старше уже известного (отброшено).
  private accept(progress: PlaybackProgressResponse): boolean {
    const ms = updatedAtMs(progress)
    const known = this.knownUpdatedAt.get(progress.episodeId)
    if (ms === null) return known === undefined
    if (known !== undefined && ms < known) return false
    this.knownUpdatedAt.set(progress.episodeId, ms)
    patchEpisodeProgress(this.queryClient, progress)
    return true
  }

  private remember(progress: PlaybackProgressResponse | null | undefined): void {
    if (!progress) return
    const ms = updatedAtMs(progress)
    const known = this.knownUpdatedAt.get(progress.episodeId)
    if (ms !== null && (known === undefined || ms > known)) {
      this.knownUpdatedAt.set(progress.episodeId, ms)
    }
  }

  private onSignal(signal: PlaybackSignal): void {
    if (signal.type === 'resync') {
      void this.resync()
      return
    }
    // Эхо своего же PUT: сервер бродкастит во все соединения, включая наше.
    if (signal.event.clientId === CLIENT_ID) return
    this.applyRemote(signal.event)
  }

  private applyRemote(event: PlaybackEvent): void {
    const progress: PlaybackProgressResponse = {
      episodeId: event.episodeId,
      position: event.position,
      completed: event.completed,
      updatedAt: event.updatedAt,
    }
    const fresh = this.accept(progress)
    const media = this.media
    if (!this.episode || this.episode.id !== event.episodeId) return
    if (!media || !this.owns() || !this.startApplied) {
      // Ещё грузимся — стартуем с самой свежей позиции.
      if (fresh) this.startProgress = progress
      return
    }
    // Прослушан на другом устройстве — как и на старте, следующий запуск с начала.
    const target = event.completed ? 0 : event.position
    if (!media.paused) {
      if (performance.now() - this.playingSince < START_GRACE_MS) return
      this.silentPause = true
      media.pause()
      this.seekSilently(target)
      this.notify(
        i18n.t(($) => $.player.continuedElsewhere),
        'info',
      )
    } else if (fresh) {
      this.seekSilently(target)
    }
  }

  // Потери NATS: после реконнекта WS и при возврате вкладки перечитываем позицию, если
  // эпизод загружен и не играет. Принимаем только строго более свежую, чем известная.
  private async resync(): Promise<void> {
    const media = this.media
    const episode = this.episode
    if (!media || !episode || !this.owns() || !this.startApplied || !media.paused) return
    let progress: PlaybackProgressResponse
    try {
      progress = await getProgress(episode.id)
    } catch {
      return
    }
    if (this.episode?.id !== episode.id || this.media !== media || !media.paused) return
    const ms = updatedAtMs(progress)
    const known = this.knownUpdatedAt.get(episode.id)
    if (ms === null || (known !== undefined && ms <= known)) return
    this.accept(progress)
    this.seekSilently(progress.completed ? 0 : progress.position)
  }
}
